import type { ProductStyleVariantsStateProps } from './ProductColor';
import type { ProductPriceHybrisProps } from './productPrice/ProductPrice';
import type { ProductDetailInfo as ProductDetailInfoSchema } from '../../../../amplienceTypes/schemas/imported/product-detail-info-schema';
import type { RuleSet } from 'styled-components';

import dynamic from 'next/dynamic';
import { useMemo, useState } from 'react';
import styled, { css } from 'styled-components';

import { ModelWearsDescription } from './ModelWearsDescription';
import { PpeMessage } from './PpeMessage';
import { ProductColor } from './ProductColor';
import { ProductDeliveryOptions } from './ProductDeliveryOptions';
import { ProductName } from './ProductName';
import { ProductNotPurchasableMessage } from './ProductNotPurchasableMessage';
import { ProductPrice } from './productPrice/ProductPrice';
import { ProductReturnFees } from './ProductReturnFees';
import { ProductUSPs } from './ProductUSPs';
import { SizeGuideBlock } from './SizeGuideBlock';
import { SizeSelector } from './sizeSelector/SizeSelector';
import { FindInStore } from './storeStockLocator/FindInStore';
import { media } from '../../../shared/core/styles';
import { FeatureFlag } from '~/components/shared/FeatureFlag';
import { CodeFeatureFlag } from '~/config/code-feature-flags';
import { useGalleryContext } from '~/utilities/context/dynamic/GalleryContext';
import { useMediaQueryContext } from '~/utilities/context/dynamic/MediaQueryContext';
import { usePDPDynamicContext } from '~/utilities/context/dynamic/PDPDynamicContext';
import { SizeProvider } from '~/utilities/context/dynamic/SizeContext';
import { useUserContext } from '~/utilities/context/dynamic/UserContext';
import { useAppContext } from '~/utilities/context/static/AppContext';
import { usePDPContext } from '~/utilities/context/static/PDPContext';
import { useStaticContext } from '~/utilities/context/static/StaticContext';
import type { Size } from '~/utilities/graphql/codegen';
import { LowestPriceDisplay, SiteType } from '~/utilities/graphql/codegen';
import { getSiteStyles } from '~/utilities/helpers';

const Fitanalytics = dynamic(
  async () => (await import('../../fitanalytics/Fitanalytics')).Fitanalytics,
  { ssr: false }
);

const GstarProductDetailInfoInner = css`
  @media ${({ theme }) => media(theme).greaterThan('sm')} {
    padding: ${({ theme }) => `${theme.size[6]} ${theme.size[8]} ${theme.size[4]}`};
  }
`;

const AafProductDetailInfoInner = css`
  padding-bottom: 40px;

  @media ${({ theme }) => media(theme).greaterThan('sm')} {
    padding: ${({ theme }) => `${theme.padding.lg} 20px 20px`};
  }

  @media ${({ theme }) => media(theme).greaterThan('lg')} {
    padding-bottom: ${({ theme }) => theme.size[8]};
  }
`;

const GstarProductDetailInfoWrapper = css`
  @media ${({ theme }) => media(theme).lessThan('sm')} {
    padding: ${({ theme }) => `${theme.padding.sm} ${theme.padding.sm}`};
  }
`;

const AafProductDetailInfoWrapper = css`
  @media ${({ theme }) => media(theme).lessThan('sm')} {
    padding: ${({ theme }) => `20px ${theme.padding.sm} 0`};
  }
`;

const ProductDetailInfoInnerConfig: Record<SiteType, RuleSet> = {
  [SiteType.Gstar]: GstarProductDetailInfoInner,
  [SiteType.Wahts]: GstarProductDetailInfoInner,
  [SiteType.Outlet]: GstarProductDetailInfoInner,
  [SiteType.Employeeshop]: GstarProductDetailInfoInner,
  [SiteType.Aaf]: AafProductDetailInfoInner,
};

const ProductDetailInfoWrapperConfig: Record<SiteType, RuleSet> = {
  [SiteType.Gstar]: GstarProductDetailInfoWrapper,
  [SiteType.Wahts]: GstarProductDetailInfoWrapper,
  [SiteType.Outlet]: GstarProductDetailInfoWrapper,
  [SiteType.Employeeshop]: GstarProductDetailInfoWrapper,
  [SiteType.Aaf]: AafProductDetailInfoWrapper,
};

const baseTitlePriceStyles = css<{ $isLarge?: boolean }>`
  min-height: ${({ $isLarge }) => ($isLarge ? '80px' : '70px')};
  margin-bottom: ${({ theme }) => theme.padding.xs};

  @media ${({ theme }) => media(theme).lessThan('sm')} {
    min-height: ${({ $isLarge }) => ($isLarge ? '74px' : '64px')};
  }
`;

const S = {
  ProductDetailInfoWrapper: styled.div<{
    $galleryZoomIn: boolean;
  }>`
    ${({ theme, $galleryZoomIn }) => css`
      --details-min-width: 360px;
      --details-width: 100%;
      --details-max-width: var(--details-width);
      ${ProductDetailInfoWrapperConfig[theme.siteType]}

      height: 100%;
      width: clamp(var(--details-min-width), var(--details-width), var(--details-max-width));
      padding: 0;
      transition: width 250ms ease;
      box-sizing: border-box;
      background-color: ${theme.colors.background.base};
      z-index: 0;

      @media (prefers-reduced-motion) {
        transition: none;
      }

      @media ${media(theme).greaterThan('lg')} {
        position: relative;
        right: 0;
        display: ${$galleryZoomIn ? 'none' : 'table'};
        top: 0;
        box-sizing: content-box;
      }

      @media (min-width: 1440px) {
        right: 0;
      }

      @media ${({ theme }) => media(theme).greaterThan('lg')} {
        --details-width: ${theme.variables.product?.details.width.desktop};
        --details-max-width: calc(${theme.variables.product?.details.width.max.desktop} + 64px);
        --details-min-width: ${theme.variables.product?.details.width.min.desktop};
      }
    `}
  `,

  ProductDetailInfoPanel: styled.div<{
    $galleryZoomIn: boolean;
  }>`
    ${({ theme, $galleryZoomIn }) => css`
      @media ${media(theme).greaterThan('lg')} {
        display: table-cell;
        position: relative;
        max-width: 240px;
        vertical-align: middle;
        z-index: ${$galleryZoomIn ? 0 : 501};
      }
    `}
  `,

  ProductDetailInfoInner: styled.div`
    ${({ theme }) => css`
      position: relative;

      @media ${media(theme).greaterThan('sm')} {
        max-width: initial;
        margin: initial;
      }

      @media ${media(theme).greaterThan('lg')} {
        max-width: var(--details-max-width);
        padding-bottom: ${theme.padding.sm};
      }

      transform-style: preserve-3d;
      backface-visibility: hidden;

      ${ProductDetailInfoInnerConfig[theme.siteType]}
    `}
  `,

  SizeGuidesWrapper: styled.div`
    display: inline-flex;
    gap: ${({ theme }) => theme.spacing.x.xs};
  `,

  ProductTitlePriceContainer: styled.div<{ $isLarge?: boolean }>`
    display: block;
    height: auto;
    width: 100%;

    /* RRP Tooltip require overflow visible
    overflow: hidden;
    transform-style: preserve-3d;
    */
    backface-visibility: hidden;
    ${({ theme }) =>
      getSiteStyles(theme.siteType, {
        [SiteType.Gstar]: baseTitlePriceStyles,
        [SiteType.Outlet]: baseTitlePriceStyles,
      })}
  `,
};

export const ProductDetailControl = ({
  productName,
  productPrice,
  productSizeSelector,
  productColor,
  productCountdownTimer,
  productStyleVariantInfo,
  productFindInStore,
  productNotPurchasableMessage,
  productSizeGuide,
  productDeliveryOptions,
}: ProductDetailInfoSchema & ProductStyleVariantsStateProps & ProductPriceHybrisProps) => {
  const {
    product: {
      isGiftCard,
      comingSoon: staticComingSoon,
      purchasable: staticPurchasable,
      showStoreStockLocator: staticShowStoreStockLocator,
      price: staticPrice,
      fromPrice: staticFromPrice,
      personalisedPrice: staticPersonalisedPrice,
      intermediaryPrice: staticIntermediaryPrice,
      formattedLowestPrice: staticFormattedLowestPrice,
      code,
      deliveryOptions,
      hasLocalStoreStock: isAvailableInLocalStore,
      sizeInformation,
      salesDiscountsEndDate,
      mainColor,
      colorData,
      oneSize,
    },
  } = usePDPContext();

  const {
    dynamicProductData: {
      comingSoon: dynamicComingSoon,
      purchasable: dynamicPurchasable,
      price: dynamicPrice,
      fromPrice: dynamicFromPrice,
      personalisedPrice: dynamicPersonalisedPrice,
      intermediaryPrice: dynamicIntermediaryPrice,
      formattedLowestPrice: dynamicFormattedLowestPrice,
      showStoreStockLocator: dynamicShowStoreStockLocator,
      sizeInformation: dynamicSizeInformation,
    },
  } = usePDPDynamicContext();
  const {
    configuration: {
      enablePdpLocalStoreStockSizeAvailability,
      lowestPriceDisplay,
      enablePdpDeliveryOptions,
    },
  } = useStaticContext();
  const { user } = useUserContext();
  const { isDesktop } = useMediaQueryContext();

  const comingSoon = dynamicComingSoon ?? staticComingSoon;
  const purchasable = dynamicPurchasable ?? staticPurchasable;
  const price = dynamicPrice ?? staticPrice;
  const showStoreStockLocator = dynamicShowStoreStockLocator ?? staticShowStoreStockLocator;
  const fromPrice = dynamicFromPrice ?? staticFromPrice;
  const personalisedPrice = dynamicPersonalisedPrice ?? staticPersonalisedPrice;
  const intermediaryPrice = dynamicIntermediaryPrice ?? staticIntermediaryPrice;
  const formattedLowestPrice = dynamicFormattedLowestPrice ?? staticFormattedLowestPrice;
  const sizes = dynamicSizeInformation?.sizes ?? sizeInformation?.sizes;

  const { galleryZoomIn } = useGalleryContext();
  const [isOutOfStockSizeSelected, setIsOutOfStockSizeSelected] = useState(false);
  const [hasLocalStoreStock, setHasLocalStoreStock] = useState(
    enablePdpLocalStoreStockSizeAvailability && isAvailableInLocalStore
  );
  const [selectedSize, setSelectedSize] = useState<Size | null | undefined>();
  const { isGStar, siteType } = useAppContext();
  const hasVariantHoverFeature =
    CodeFeatureFlag[siteType].showProductDetailsStyleVariants && isDesktop;

  const hasSizeMetadata = !comingSoon && !isGiftCard && !oneSize;

  const showSizeSelector = useMemo(
    () =>
      purchasable ||
      comingSoon ||
      sizes?.some(size => !size?.purchasable && size?.notPurchasableDueToStock),
    [purchasable, comingSoon, sizes]
  );

  return (
    <S.ProductDetailInfoWrapper
      className="product-detail-info-wrapper"
      suppressHydrationWarning
      $galleryZoomIn={galleryZoomIn}
    >
      <S.ProductDetailInfoPanel $galleryZoomIn={galleryZoomIn}>
        <S.ProductDetailInfoInner>
          <S.ProductTitlePriceContainer
            $isLarge={
              !!user?.personalisedDiscount && lowestPriceDisplay !== LowestPriceDisplay.Never
            }
          >
            {productName?.content && (
              <ProductName
                {...productName.content}
                productStyleVariantInfo={hasVariantHoverFeature ? {} : productStyleVariantInfo}
              />
            )}
            {productPrice?.content && (
              <ProductPrice
                {...productPrice.content}
                productStyleVariantInfo={hasVariantHoverFeature ? {} : productStyleVariantInfo}
                price={price}
                fromPrice={fromPrice}
                personalisedPrice={personalisedPrice}
                intermediaryPrice={intermediaryPrice}
                formattedLowestPrice={formattedLowestPrice}
              />
            )}
            <PpeMessage
              hasDiscountPrice={
                !!fromPrice?.formattedValue && (fromPrice.value ?? 0) > (price?.value ?? 0)
              }
              salesDiscountsEndDate={salesDiscountsEndDate}
              productCode={code}
            />
          </S.ProductTitlePriceContainer>
          <FeatureFlag flag="showProductDetailsStyleVariants">
            {productColor?.content && (
              <ProductColor
                {...productColor.content}
                code={code}
                color={mainColor}
                colorData={colorData}
              />
            )}
          </FeatureFlag>
          <SizeProvider>
            {hasSizeMetadata && productSizeGuide?.content && <ModelWearsDescription />}
            {!purchasable && !comingSoon && productNotPurchasableMessage?.content && (
              <ProductNotPurchasableMessage
                {...productNotPurchasableMessage.content}
                selectedSize={selectedSize}
              />
            )}
            {showSizeSelector && productSizeSelector?.content && (
              <SizeSelector
                {...(productSizeGuide?.content ?? {})}
                {...productSizeSelector.content}
                productCountdownTimer={!comingSoon ? productCountdownTimer : undefined}
                isOutOfStockSizeSelected={isOutOfStockSizeSelected}
                setIsOutOfStockSizeSelected={setIsOutOfStockSizeSelected}
                setHasLocalStoreStock={setHasLocalStoreStock}
                setSelectedSize={setSelectedSize}
                selectedSize={selectedSize}
              >
                {isGStar && (
                  <S.SizeGuidesWrapper>
                    {hasSizeMetadata && (
                      <>
                        <Fitanalytics sizeInformation={sizeInformation} />

                        {productSizeGuide?.content && (
                          <SizeGuideBlock labelProps={productSizeGuide.content} />
                        )}
                      </>
                    )}
                  </S.SizeGuidesWrapper>
                )}
              </SizeSelector>
            )}
            {!isGiftCard && showStoreStockLocator && !comingSoon && productFindInStore?.content && (
              <FindInStore
                {...productFindInStore.content}
                isHidden={isOutOfStockSizeSelected}
                hasLocalStoreStock={hasLocalStoreStock ?? false}
              />
            )}
            {!isOutOfStockSizeSelected &&
              purchasable &&
              !isGiftCard &&
              showStoreStockLocator &&
              !dynamicComingSoon &&
              !enablePdpDeliveryOptions && <ProductUSPs />}
            {!isOutOfStockSizeSelected &&
              !isGiftCard &&
              purchasable &&
              deliveryOptions?.deliveryOptions &&
              deliveryOptions.deliveryOptions.length > 0 && (
                <ProductDeliveryOptions
                  deliveryOptions={deliveryOptions.deliveryOptions}
                  content={productDeliveryOptions?.content}
                />
              )}
            {!isOutOfStockSizeSelected && <ProductReturnFees />}
          </SizeProvider>
        </S.ProductDetailInfoInner>
      </S.ProductDetailInfoPanel>
    </S.ProductDetailInfoWrapper>
  );
};
